import { useEffect, useRef, useState } from "react";
import { IconChevronDown } from "@tabler/icons-react";

import { Button, Popper, Text } from "@fragments";

import { MONTH_LABELS_LONG, MONTH_LABELS_SHORT } from "../../../hooks/dateUtils";

import "./styles.scss";

const POPPER_PAPER_SX = {
    minWidth: 240,
    maxWidth: 260,
    width: 256,
};

export default function MonthHeader({ year, month, minDate, maxDate, onChange }) {
    const popperRef = useRef(null);
    const triggerRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);

    function handleOpen() {
        popperRef.current?.open(
            [],
            "bottom",
            triggerRef.current,
            <MonthYearGrid
                year={year}
                month={month}
                minDate={minDate}
                maxDate={maxDate}
                onPick={(y, m) => {
                    onChange(y, m);
                    popperRef.current?.close();
                }}
            />,
        );
    }

    return (
        <>
            <Button
                ref={triggerRef}
                className={`dpi-month-header ${isOpen ? "is-open" : ""}`}
                text={`${MONTH_LABELS_LONG[month]} de ${year}`}
                color="overlay"
                size="small"
                endIcon={<IconChevronDown size={14} stroke={2.2} />}
                onClick={handleOpen}
                preventLoading
            />

            <Popper
                controlRef={popperRef}
                paperSx={POPPER_PAPER_SX}
                onOpenChange={setIsOpen}
            >
                <span />
            </Popper>
        </>
    );
}

function isMonthDisabled(y, m, minDate, maxDate) {
    if (maxDate) {
        const maxY = maxDate.getFullYear();
        const maxM = maxDate.getMonth();
        if (y > maxY) return true;
        if (y === maxY && m > maxM) return true;
    }
    if (minDate) {
        const minY = minDate.getFullYear();
        const minM = minDate.getMonth();
        if (y < minY) return true;
        if (y === minY && m < minM) return true;
    }
    return false;
}

function MonthYearGrid({ year, month, minDate, maxDate, onPick }) {
    const containerRef = useRef(null);
    const activeRef = useRef(null);

    useEffect(() => {
        if (activeRef.current && containerRef.current) {
            const c = containerRef.current;
            const target = activeRef.current;
            c.scrollTop = target.offsetTop - c.clientHeight / 2 + target.clientHeight / 2;
        }
    }, []);

    const minYearBound = minDate?.getFullYear() ?? year - 7;
    const maxYearBound = maxDate?.getFullYear() ?? year + 7;
    const startYear = Math.max(year - 7, minYearBound);
    const endYear = Math.min(year + 7, maxYearBound);

    const years = [];
    for (let y = startYear; y <= endYear; y += 1) years.push(y);

    return (
        <div className="dpi-month-year">
            <button
                type="button"
                className="dpi-month-year__current"
                onClick={() => onPick(year, month)}
            >
                <Text.span contrast={1}>
                    {`${MONTH_LABELS_LONG[month]} de ${year}`}
                </Text.span>
            </button>

            <div className="dpi-month-year__scroll" ref={containerRef}>
                {years.map((y) => (
                    <div key={y} className="dpi-month-year__year-block">
                        <div className="dpi-month-year__year-label">
                            <Text.span contrast={9}>{y}</Text.span>
                        </div>

                        <div className="dpi-month-year__months">
                            {MONTH_LABELS_SHORT.map((label, m) => {
                                const isActive = y === year && m === month;
                                const disabled = isMonthDisabled(y, m, minDate, maxDate);
                                const classes = [
                                    "dpi-month-year__cell",
                                    isActive && "is-active",
                                    disabled && "is-disabled",
                                ].filter(Boolean).join(" ");
                                return (
                                    <button
                                        type="button"
                                        key={`${y}-${m}`}
                                        ref={isActive ? activeRef : null}
                                        className={classes}
                                        onClick={() => !disabled && onPick(y, m)}
                                        disabled={disabled}
                                    >
                                        <Text.span contrast={isActive ? 1 : disabled ? 5 : 8}>
                                            {label}
                                        </Text.span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
